import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import DownloadForOfflineOutlinedIcon from '@mui/icons-material/DownloadForOfflineOutlined';
import {
  Alert,
  Button,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import { Fragment, useContext } from 'react';

import { useGetInvoices } from '@features/payments/hooks/useGetInvoices';
import { white } from '@styles/colors';
import { medium } from '@styles/fontWeights';
import { useGetCurrentPlan } from '@features/payments/hooks/useGetCurrentPlan';

const formatCentsToDollars = (cents: number) => {
  const dollars = (cents / 100).toFixed(2);
  return dollars.endsWith('.00') ? `$${parseInt(dollars)}` : `$${dollars}`;
};

const InvoicesTab = () => {
  const {
    authData: { user },
  } = useContext(authContext);

  const { currentPlanData } = useGetCurrentPlan();

  const { invoicesData, isLoadingInvoicesData } = useGetInvoices();

  const getBillingStatus = () => {
    if (user?.enterprise?.plan === 999) {
      return 'Free Trial';
    }

    if (currentPlanData?.status === 200) {
      return 'Active';
    }

    return 'Inactive';
  };

  return (
    <Stack
      spacing={3}
      bgcolor={white}
      p={3}
      borderRadius="8px"
      width="100%"
      alignItems="center"
    >
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="center"
        justifyContent="space-around"
        width="100%"
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4" style={{ fontWeight: '600' }}>
            Billing Status:
          </Typography>
          <Typography
            color="text.secondary"
            fontStyle="italic"
            variant="subtitle1"
          >
            {getBillingStatus()}
          </Typography>
        </Stack>
      </Stack>

      <Divider flexItem />
      {isLoadingInvoicesData ? (
        <CircularProgress />
      ) : invoicesData?.object?.length ? (
        <>
          <Typography variant="h4" style={{ fontWeight: '600' }}>
            Invoices
          </Typography>
          <List disablePadding sx={{ width: '100%' }}>
            {invoicesData?.object.map(
              (invoice: {
                id: number;
                paid: boolean;
                created: number;
                invoice_pdf: string;
                total: number;
                number: string;
              }) => (
                <Fragment key={invoice.id}>
                  <ListItem>
                    <ListItemIcon>
                      {invoice.paid ? (
                        <CheckCircleOutlineOutlinedIcon
                          style={{ fontSize: '18px' }}
                          color="success"
                        />
                      ) : (
                        <CancelOutlinedIcon
                          style={{ fontSize: '18px' }}
                          color="error"
                        />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography color="text.secondary" fontSize="14px">
                          {dayjs(invoice.created * 1000).format('MM/DD/YYYY')}
                        </Typography>
                      }
                    />
                    <ListItemText
                      primary={
                        <Typography fontWeight={medium}>
                          {formatCentsToDollars(invoice.total)}
                        </Typography>
                      }
                    />
                    <Button
                      startIcon={
                        <DownloadForOfflineOutlinedIcon
                          style={{ fontSize: '16px' }}
                        />
                      }
                      variant="contained"
                      color="success"
                      onClick={() =>
                        saveAs(
                          invoice.invoice_pdf,
                          `invoice-${invoice.number}.pdf`,
                        )
                      }
                      sx={{
                        padding: '4px 15px',
                        fontSize: '14px',
                      }}
                    >
                      Print / Download
                    </Button>
                  </ListItem>
                  <Divider />
                </Fragment>
              ),
            )}
          </List>
        </>
      ) : (
        <Alert severity="info" sx={{ width: '100%' }}>
          There are no invoices yet
        </Alert>
      )}
    </Stack>
  );
};

export default InvoicesTab;
